import { useMemo } from 'react';

import type { EnterpriseProperties } from '@coursera/event-pulse-types';

import { useResolvedProgramIds } from 'bundles/unified-common/contexts/org-scoped/OrgScopedImpersonationContext';
import {
  useOrgSlug,
  useThirdPartyOrganization,
} from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';

/**
 * Event-data block we attach to V3 events on unified discovery surfaces
 * (browse, home, my-learning, skillsets, etc.).
 *
 * Uses server-resolved program IDs from the Program Resolver API — the
 * canonical source that handles standard users, admin impersonation, and
 * logged-out visitors. On routes with no enterprise context it returns `{}`,
 * leaving event data unchanged.
 */
const useUnifiedDiscoveryTrackingProperties = (): { enterprise?: EnterpriseProperties } => {
  const { programIds: resolvedProgramIds } = useResolvedProgramIds();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const orgSlug = useOrgSlug();

  return useMemo(() => {
    const organizationId = thirdPartyOrganization?.id;

    if (!organizationId && !orgSlug && resolvedProgramIds.length === 0) {
      return {};
    }

    return {
      enterprise: {
        organizationSlug: orgSlug,
        organizationId,
        programIds: resolvedProgramIds,
      },
    };
  }, [resolvedProgramIds, thirdPartyOrganization?.id, orgSlug]);
};

export default useUnifiedDiscoveryTrackingProperties;
